import React, { useState, useEffect } from 'react';
import { apiClient } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const NewIncomeForm = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    patient_id: '',
    category: 'consultation',
    service_id: '',
    amount: '',
    payment_type: 'cash',
    payment_method: 'Cash',
    // HMO/PhilHealth
    provider_name: '',
    approval_code: '',
    claim_status: 'pending',
    // Credit/Promissory
    payment_status: 'paid',
    amount_paid: '',
    due_date: '',
    // Standard
    waived_amount: '0',
    discount_amount: '0',
    notes: ''
  });

  useEffect(() => {
    fetchPatients();
    fetchServices();
  }, []);

  const fetchPatients = async () => {
    try {
      const response = await apiClient.get('/patients');
      setPatients(response.data);
    } catch (error) {
      console.error('Failed to fetch patients');
    }
  };

  const fetchServices = async () => {
    try {
      const response = await apiClient.get('/services?active_only=true');
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services');
    }
  };

  const handleServiceSelect = (serviceId) => {
    const service = services.find(s => s.service_id === serviceId);
    if (service) {
      setForm({
        ...form,
        service_id: serviceId,
        category: service.category,
        amount: service.default_price.toString()
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.category || !form.amount) {
      toast.error('Category and amount are required');
      return;
    }

    // Validation for credit/partial
    if (form.payment_status === 'credit' && !form.due_date) {
      toast.error('Due date is required for credit/promissory');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        waived_amount: parseFloat(form.waived_amount) || 0,
        discount_amount: parseFloat(form.discount_amount) || 0,
        amount_paid: form.payment_status === 'partial' ? parseFloat(form.amount_paid) || 0 : null,
        patient_id: form.patient_id || null,
        service_id: form.service_id || null,
        provider_name: form.payment_type in ['hmo', 'philhealth'] ? form.provider_name : null,
        approval_code: form.payment_type in ['hmo', 'philhealth'] ? form.approval_code : null,
        claim_status: ['hmo', 'philhealth'].includes(form.payment_type) ? form.claim_status : null,
      };

      await apiClient.post('/income', payload);
      toast.success('Income recorded');
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record income');
    } finally {
      setLoading(false);
    }
  };

  const showHmoFields = ['hmo', 'philhealth'].includes(form.payment_type);
  const showCreditFields = ['partial', 'credit'].includes(form.payment_status);

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div>
        <Label>Patient (optional)</Label>
        <Select value={form.patient_id || "none"} onValueChange={(v) => setForm({ ...form, patient_id: v === "none" ? "" : v })}>
          <SelectTrigger data-testid="income-patient-select">
            <SelectValue placeholder="Select patient (optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No patient selected</SelectItem>
            {patients.map(p => (
              <SelectItem key={p.patient_id} value={p.patient_id}>
                {p.full_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Service Selection */}
      {services.length > 0 && (
        <div>
          <Label>Service (auto-fills price)</Label>
          <Select value={form.service_id || "manual"} onValueChange={(v) => v === "manual" ? setForm({ ...form, service_id: "" }) : handleServiceSelect(v)}>
            <SelectTrigger data-testid="income-service-select">
              <SelectValue placeholder="Select service (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Manual entry</SelectItem>
              {services.map(s => (
                <SelectItem key={s.service_id} value={s.service_id}>
                  {s.name} - ₱{s.default_price}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Category *</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="income-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="consultation">Consultation</SelectItem>
              <SelectItem value="procedure">Procedure</SelectItem>
              <SelectItem value="laboratory">Laboratory</SelectItem>
              <SelectItem value="medicine">Medicine</SelectItem>
              <SelectItem value="rx_copy">RX Copy</SelectItem>
              <SelectItem value="lab_copy">Lab Copy</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Payment Type *</Label>
          <Select value={form.payment_type} onValueChange={(v) => setForm({ ...form, payment_type: v })}>
            <SelectTrigger data-testid="income-payment-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="gcash_maya">GCash / Maya</SelectItem>
              <SelectItem value="hmo">HMO</SelectItem>
              <SelectItem value="philhealth">PhilHealth</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* HMO/PhilHealth Fields */}
      {showHmoFields && (
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
          <p className="text-sm font-medium text-amber-800">
            {form.payment_type === 'hmo' ? 'HMO' : 'PhilHealth'} Details
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Provider Name</Label>
              <Input
                value={form.provider_name}
                onChange={(e) => setForm({ ...form, provider_name: e.target.value })}
                placeholder="e.g., Maxicare, Medicard"
              />
            </div>
            <div>
              <Label className="text-xs">Approval Code</Label>
              <Input
                value={form.approval_code}
                onChange={(e) => setForm({ ...form, approval_code: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Claim Status</Label>
            <Select value={form.claim_status} onValueChange={(v) => setForm({ ...form, claim_status: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div>
        <Label>Amount (PHP) *</Label>
        <Input
          type="number"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          placeholder="0.00"
          data-testid="income-amount-input"
        />
      </div>

      {/* Payment Status */}
      <div>
        <Label>Payment Status</Label>
        <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
          <SelectTrigger data-testid="income-payment-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="paid">Fully Paid</SelectItem>
            <SelectItem value="partial">Partial Payment</SelectItem>
            <SelectItem value="credit">Credit / Promissory</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Credit/Partial Fields */}
      {showCreditFields && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
          <p className="text-sm font-medium text-blue-800">
            {form.payment_status === 'partial' ? 'Partial Payment' : 'Credit / Promissory'} Details
          </p>
          {form.payment_status === 'partial' && (
            <div>
              <Label className="text-xs">Amount Paid Now *</Label>
              <Input
                type="number"
                value={form.amount_paid}
                onChange={(e) => setForm({ ...form, amount_paid: e.target.value })}
                placeholder="0.00"
              />
              {form.amount && form.amount_paid && (
                <p className="text-xs text-muted-foreground mt-1">
                  Balance: ₱{(parseFloat(form.amount) - parseFloat(form.amount_paid)).toFixed(2)}
                </p>
              )}
            </div>
          )}
          <div>
            <Label className="text-xs">Due Date {form.payment_status === 'credit' ? '*' : ''}</Label>
            <Input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Waived Amount (Pro Bono)</Label>
          <Input
            type="number"
            value={form.waived_amount}
            onChange={(e) => setForm({ ...form, waived_amount: e.target.value })}
            placeholder="0.00"
          />
        </div>
        
        <div>
          <Label>Discount Amount</Label>
          <Input
            type="number"
            value={form.discount_amount}
            onChange={(e) => setForm({ ...form, discount_amount: e.target.value })}
            placeholder="0.00"
          />
        </div>
      </div>

      <div>
        <Label>Notes</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Any additional notes..."
          rows={2}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="submit" disabled={loading} data-testid="submit-income-btn">
          {loading ? 'Saving...' : 'Log Income'}
        </Button>
      </div>
    </form>
  );
};

export default NewIncomeForm;
